/** アセットの種類 */
export enum AssetType {
  /** 画像（PNG/JPG等） */
  IMAGE = 'IMAGE',
  /** スプライトシート */
  SPRITESHEET = 'SPRITESHEET',
  /** オーディオ */
  AUDIO = 'AUDIO',
  /** JSONデータ */
  JSON = 'JSON',
}

/** アセット定義の基本インターフェース */
export interface AssetEntry {
  /** アセットの一意キー（Phaserのキャッシュキー） */
  key: string;
  /** アセットの種類 */
  type: AssetType;
  /** ファイルパス */
  path: string;
}

/** スプライトシート固有の追加情報 */
export interface SpritesheetAssetEntry extends AssetEntry {
  type: AssetType.SPRITESHEET;
  /** フレーム幅 */
  frameWidth: number;
  /** フレーム高さ */
  frameHeight: number;
}

/** アセットマニフェスト全体 */
export interface AssetManifest {
  /** アセット一覧 */
  assets: (AssetEntry | SpritesheetAssetEntry)[];
}

/**
 * アセットマニフェスト
 */
export const ASSET_MANIFEST: AssetManifest = {
  assets: [
    // ─── 優先度高：最初に表示される背景・UI（タイムアウト前に確実にロード） ───
    { key: 'bg_title',            type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_title.webp' },
    { key: 'title_logo',          type: AssetType.IMAGE, path: 'assets/images/backgrounds/title_logo.webp' },
    { key: 'bg_character_select', type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_character_select.webp' },
    { key: 'bg_mode_select',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_mode_select.webp' },
    { key: 'bg_result',           type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_result.webp' },
    { key: 'bg_settings',         type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_settings.webp' },
    { key: 'ui_lock_icon',        type: AssetType.IMAGE, path: 'assets/images/monsters/lock.webp' },
    { key: 'btn_command',         type: AssetType.IMAGE, path: 'assets/images/ui/btn_command.webp' },
    { key: 'btn_command_active',  type: AssetType.IMAGE, path: 'assets/images/ui/btn_command_active.webp' },
    // モンスター画像（バトル用＆ポートレート用 — 同一ファイル）
    { key: 'monster_zaag_battle',    type: AssetType.IMAGE, path: 'assets/images/monsters/zaag.webp' },
    { key: 'monster_zaag_portrait',  type: AssetType.IMAGE, path: 'assets/images/monsters/zaag.webp' },
    { key: 'monster_gardan_battle',  type: AssetType.IMAGE, path: 'assets/images/monsters/gardan.webp' },
    { key: 'monster_gardan_portrait',type: AssetType.IMAGE, path: 'assets/images/monsters/gardan.webp' },
    { key: 'monster_roona_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/roona.webp' },
    { key: 'monster_roona_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/roona.webp' },
    { key: 'monster_zephyr_battle',  type: AssetType.IMAGE, path: 'assets/images/monsters/zephyr.webp' },
    { key: 'monster_zephyr_portrait',type: AssetType.IMAGE, path: 'assets/images/monsters/zephyr.webp' },
    { key: 'monster_balga_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/balga.webp' },
    { key: 'monster_balga_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/balga.webp' },
    { key: 'monster_morsu_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/morsu.webp' },
    { key: 'monster_morsu_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/morsu.webp' },
    { key: 'monster_graon_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/graon.webp' },
    { key: 'monster_graon_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/graon.webp' },
    { key: 'monster_igna_battle',    type: AssetType.IMAGE, path: 'assets/images/monsters/igna.webp' },
    { key: 'monster_igna_portrait',  type: AssetType.IMAGE, path: 'assets/images/monsters/igna.webp' },
    { key: 'monster_erebos_battle',       type: AssetType.IMAGE, path: 'assets/images/monsters/erebos.webp' },
    { key: 'monster_erebos_portrait',     type: AssetType.IMAGE, path: 'assets/images/monsters/erebos.webp' },
    { key: 'monster_erebos_form2_battle', type: AssetType.IMAGE, path: 'assets/images/monsters/erebos2.webp' },
    { key: 'monster_erebos_form2_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/erebos2.webp' },
    { key: 'monster_alice_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/alice.webp' },
    { key: 'monster_alice_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/alice.webp' },
    { key: 'monster_mococo_battle',   type: AssetType.IMAGE, path: 'assets/images/monsters/mococo.webp' },
    { key: 'monster_mococo_portrait', type: AssetType.IMAGE, path: 'assets/images/monsters/mococo.webp' },
    // オーブ画像
    { key: 'orb_flame',   type: AssetType.IMAGE, path: 'assets/images/orbs/flame_orb_128x128.webp' },
    { key: 'orb_rock',    type: AssetType.IMAGE, path: 'assets/images/orbs/rock_orb_128x128.webp' },
    { key: 'orb_wisp',    type: AssetType.IMAGE, path: 'assets/images/orbs/wisp_orb_128x128.webp' },
    { key: 'orb_wind',    type: AssetType.IMAGE, path: 'assets/images/orbs/wind_orb_128x128.webp' },
    { key: 'orb_water',   type: AssetType.IMAGE, path: 'assets/images/orbs/water_orb_128x128.webp' },
    { key: 'orb_leaf',    type: AssetType.IMAGE, path: 'assets/images/orbs/leaf_orb_128x128.webp' },
    { key: 'orb_claw',    type: AssetType.IMAGE, path: 'assets/images/orbs/claw_orb_128x128.webp' },
    { key: 'orb_neutral', type: AssetType.IMAGE, path: 'assets/images/orbs/neutral_orb_128x128.webp' },
    { key: 'orb_erebos',       type: AssetType.IMAGE, path: 'assets/images/orbs/erebos_orb_128x128.png' },
    { key: 'orb_erebos_form2', type: AssetType.IMAGE, path: 'assets/images/orbs/erebos2_orb_128x128.png' },
    { key: 'orb_doll',         type: AssetType.IMAGE, path: 'assets/images/orbs/doll_orb_128x128.webp' },
    { key: 'orb_cursed_fluff', type: AssetType.IMAGE, path: 'assets/images/orbs/cursed_fluff_orb_128x128.webp' },
    // ─── バトル背景（戦闘画面で使用） ───
    { key: 'bg_battle',        type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle.webp' },
    { key: 'bg_battle_2',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_2.webp' },
    { key: 'bg_battle_3',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_3.webp' },
    { key: 'bg_battle_4',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_4.webp' },
    { key: 'bg_battle_5',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_5.webp' },
    { key: 'bg_battle_6',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_6.webp' },
    { key: 'bg_battle_7',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_7.webp' },
    { key: 'bg_battle_luna',   type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_luna.webp' },
    { key: 'bg_battle_state8', type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_state8.webp' },
    { key: 'bg_battle_boss_1', type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_boss_1.webp' },
    { key: 'bg_battle_boss_2', type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_boss_2.webp' },
    { key: 'boss_result',      type: AssetType.IMAGE, path: 'assets/images/backgrounds/boss_result.webp' },
    { key: 'bg_battle_arena',  type: AssetType.IMAGE, path: 'assets/images/backgrounds/bg_battle_arena.webp' },
    // ─── オーディオ（BGM・SE）───
    { key: 'bgm_title', type: AssetType.AUDIO, path: 'assets/audio/bgm/title.mp3' },
    { key: 'bgm_character_select', type: AssetType.AUDIO, path: 'assets/audio/bgm/character_select.mp3' },
    { key: 'bgm_battle', type: AssetType.AUDIO, path: 'assets/audio/bgm/battle.mp3' },
    { key: 'bgm_tournament', type: AssetType.AUDIO, path: 'assets/audio/bgm/tournament.mp3' },
    { key: 'bgm_survival_1', type: AssetType.AUDIO, path: 'assets/audio/bgm/survival1.mp3' },
    { key: 'bgm_survival_2', type: AssetType.AUDIO, path: 'assets/audio/bgm/survival2.mp3' },
    { key: 'bgm_survival_3', type: AssetType.AUDIO, path: 'assets/audio/bgm/survival3.mp3' },
    { key: 'bgm_result_victory', type: AssetType.AUDIO, path: 'assets/audio/bgm/result_victory.mp3' },
    { key: 'bgm_result_defeat', type: AssetType.AUDIO, path: 'assets/audio/bgm/result_defeat.mp3' },
    { key: 'bgm_boss', type: AssetType.AUDIO, path: 'assets/audio/bgm/boss.mp3' },
    { key: 'se_weapon_attack', type: AssetType.AUDIO, path: 'assets/audio/se/weapon_attack.mp3' },
    { key: 'se_special_attack', type: AssetType.AUDIO, path: 'assets/audio/se/special_attack.mp3' },
    { key: 'se_dodge', type: AssetType.AUDIO, path: 'assets/audio/se/dodge.mp3' },
    { key: 'se_move', type: AssetType.AUDIO, path: 'assets/audio/se/move.mp3' },
    { key: 'se_reflect', type: AssetType.AUDIO, path: 'assets/audio/se/reflect.mp3' },
    { key: 'se_reflect_block', type: AssetType.AUDIO, path: 'assets/audio/se/block.mp3' },
    { key: 'se_select', type: AssetType.AUDIO, path: 'assets/audio/se/select.mp3' },
    { key: 'se_victory', type: AssetType.AUDIO, path: 'assets/audio/se/victory.mp3' },
    { key: 'se_defeat', type: AssetType.AUDIO, path: 'assets/audio/se/defeat.mp3' },
  ],
};

/**
 * マニフェストから指定タイプのアセットのみ取得する
 */
export function getAssetsByType(manifest: AssetManifest, type: AssetType): AssetEntry[] {
  return manifest.assets.filter((a) => a.type === type);
}

/**
 * マニフェストから指定キーのアセットを取得する
 */
export function getAssetByKey(manifest: AssetManifest, key: string): AssetEntry | undefined {
  return manifest.assets.find((a) => a.key === key);
}

/**
 * マニフェストのバリデーション（キーの重複チェック等）
 */
export function validateManifest(manifest: AssetManifest): { valid: boolean; errors: string[] } {
  const errors: string[] = [];
  const keys = manifest.assets.map((a) => a.key);
  const duplicates = keys.filter((k, i) => keys.indexOf(k) !== i);
  if (duplicates.length > 0) {
    errors.push(`重複キー: ${[...new Set(duplicates)].join(', ')}`);
  }
  for (const asset of manifest.assets) {
    if (!asset.key || asset.key.trim() === '') {
      errors.push('空のアセットキーが存在します');
    }
    if (!asset.path || asset.path.trim() === '') {
      errors.push(`アセット "${asset.key}" のパスが空です`);
    }
  }
  return { valid: errors.length === 0, errors };
}
